import { UserClientService } from '../../../../../../services/authentication/user-client.service';
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { UserClaimsService } from '../../../../../../services/authentication/user-claims.service';
import { autoinject, transient } from "aurelia-dependency-injection";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { ActiveReportSubscriptionReportModel, PurchasableReportModel, PurchasedReportModel } from "../../../../../../models/reports/reports.models";
import { ReportsService } from "../../../../../../services/reports/reports.service";
import { Claims } from '../../../../../../constants/authentication/claims';
import { CompRightStoreProductModel } from '../../../../../../models/rightstore/rightstore.models';
import { RightShipDialogServiceConstants } from '../../../../../../constants/basics/rightship-dialog.constants';
import { RightStoreService } from '../../../../../../services/rightstore/rightstore.service';
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { CustomerService } from '../../../../../../services/modules/customer-client.service';
import { EventAggregator } from 'aurelia-event-aggregator';
import { Messages } from '../../../../../../configuration/event-messages.constants';
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';
import { useView } from 'aurelia-framework';

@autoinject()
@transient()
@useView('views/pages/modules/rightship-insights/common/insights/insights.html')
export class Insights {

    public activeReportSubscriptions: ActiveReportSubscriptionReportModel[];
    public purchasableReports: PurchasableReportModel[];
    public pendingMetaReports: PurchasedReportModel[];
    public purchasedReports: PurchasedReportModel[];
    public isRightStoreEnabled: boolean;
    public showInsightsBetaAndFreeTrialBanner: boolean;
    public entityId: number;
    public entityTypeId: number;
    public entityType: string;

    constructor(
        private reportsService: ReportsService, private router: Router, private userClaimsService: UserClaimsService,
        private dialogClientService: DialogClientService, private userClientService: UserClientService,
        private rightstoreService: RightStoreService,
        private customerService: CustomerService,
        private commonCustomerService: CommonCustomerService,
        private eventAggregator: EventAggregator,
        private configService: ConfigService) {
        this.showInsightsBetaAndFreeTrialBanner = this.userClaimsService.hasPermission(Claims.ShowInsightsBetaAndFreeTrialBanner);
        this.isRightStoreEnabled = this.userClaimsService.hasPermission(Claims.IsRightStoreEnabled);
    }

    activate(model: { entityId: number, entityTypeId: number }) {
        this.entityId = model.entityId;
        this.entityTypeId = model.entityTypeId;

        if (this.entityTypeId == EntityTypes.Company) {
            this.entityType = 'companyId'
        } else if (this.entityTypeId == EntityTypes.Vessel) {
            this.entityType = 'vesselId'
        }
    }

    async attached() {
        const response = await this.reportsService.getReports(this.entityId, this.entityTypeId);
        this.activeReportSubscriptions = response.activeReportSubscriptions;
        this.purchasableReports = response.purchasableReports;
        this.pendingMetaReports = response.pendingMetaReports;
        this.purchasedReports = response.purchasedReports;
    }

    openReport(report: ActiveReportSubscriptionReportModel) {
        if (report.isComingSoon) {
            return;
        }

        this.eventAggregator.publish(Messages.cardClick, { "cardCategory": "Company Active Subscription", "card": report.reportName });

        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.PowerBiReport, {
            id: report.reportId,
            title: report.reportName,
            entityTypeId: this.entityTypeId,
            entityId: this.entityId
        });
    }

    getActiveSubProductModel(item: ActiveReportSubscriptionReportModel): CompRightStoreProductModel {
        return {
            description: item.description,
            imageUrl: item.imageUrl,
            name: item.reportName,
            tags: [],
            url: null,
            isComingSoon: item.isComingSoon,
            expiry: null,
            showInPlatform: true
        }
    }

    getPurchasedProductModel(item: PurchasedReportModel): CompRightStoreProductModel {
        return {
            description: item.description,
            imageUrl: item.imageUrl,
            name: item.name,
            tags: [],
            url: item.url,
            isComingSoon: false,
            expiry: item.expiryDate,
            showInPlatform: item.showInPlatform
        }
    }

    getPurchasableProductModel(item: PurchasableReportModel): CompRightStoreProductModel {
        return {
            description: item.description,
            imageUrl: item.imageUrl,
            name: item.name,
            tags: [],
            url: item.url,
            isComingSoon: item.isComingSoon,
            expiry: null,
            showInPlatform: true
        }
    }

    onPendingProductClicked(purchasedReport: PurchasedReportModel) {

        //Check if the user clicking the product is the same that purchased it
        if (this.userClientService.currentUser.email == purchasedReport.purchasedByEmail) {
            this.navigateToOrderPage(purchasedReport);
        }
        else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PurchasedByAnotherUserDialog,
                {
                    purchasedBy: purchasedReport.purchasedBy,
                    proceed: () => {
                        this.navigateToOrderPage(purchasedReport)
                    }
                }, true);
        }
    }

    onPurchasedProductClicked(purchasedReport: PurchasedReportModel) {

        this.eventAggregator.publish(Messages.cardClick, { "cardCategory": "Company Purchased", "card": this.getPurchasedProductModel(purchasedReport).name });
        this.navigateToOrderPage(purchasedReport);
    }

    navigateToOrderPage(purchasedReport: PurchasedReportModel) {

        this.rightstoreService.getOrderUrl(purchasedReport.orderGuid).then(orderUrl => {
            //If the order url is not a report url, append the Id query string
            if (orderUrl.url.indexOf('/report') == -1) {
                const url = new URL(orderUrl.url);
                if (url.searchParams.get(this.entityType)) {
                    url.searchParams.set(this.entityType, this.entityId.toString());
                } else {
                    url.searchParams.append(this.entityType, this.entityId.toString());
                }
                location.href = url.toString();
            } else {
                location.href = orderUrl.url;
            }
        });
    }

    navigateToProductPage(url: string) {
        if (url) {
            window.open(`${url}?${this.entityType}=${this.entityId}`, "_self");
        }
    }

    async purchaseReportProduct(purchasableReport: PurchasableReportModel) {

        this.eventAggregator.publish(Messages.cardClick, { "cardCategory": "Company Purchasable", "card": purchasableReport.name });
        let customerId = this.userClientService.currentUser.settings.customerId;
        if (customerId == null || customerId <= 0) {
            customerId = await this.commonCustomerService.getCustomerId();
        }

        let businessUnitId = this.userClientService.currentUser.settings.businessUnitId;
        if (businessUnitId == null || businessUnitId <= 0) {
            businessUnitId = await this.commonCustomerService.getBusinessUnitId();
        }

        const queryParams = this.configService.config.environment.toLowerCase() == "production" ?
            `${this.entityType}=${this.entityId}&shouldAutoSubmit=true` :
            `${this.entityType}=${this.entityId}&shouldAutoSubmit=true&env=${this.configService.config.environment}`;

        const customerModel = customerId > 0 ? await this.customerService.getCustomer(customerId) : null;

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightstoreCheckoutDialog,
            {
                productNumber: purchasableReport.productCode,
                queryParams: queryParams,
                customerNumber: businessUnitId,
                userName: this.userClientService.currentUser.email,
                canCustomerInvoice: customerModel?.customer?.canInvoice ?? true,
                afterPurchase: (async (url: string) => { 
                    const orderId = new URL(url).pathname.split('order/').pop()?.split(/[/|?]/)?.find(Boolean);
                    const response = await this.rightstoreService.saveOrderMeta(orderId, JSON.stringify({ "companyId": this.entityId }));
                    const orderUrlResponse = await this.rightstoreService.getOrderUrl(response.orderGuid);
                    if (orderUrlResponse.url.startsWith(location.origin)) {
                        this.router.navigate(orderUrlResponse.url.replace(location.origin, ""), { replace: true, trigger: true });
                    }
                    else {
                        location.href = orderUrlResponse.url;
                    }
                }).bind(this)
            }, true, false, false, 'right');
    }
}